import { Calendar } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { useMemo, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Flex, Heading, Text, TextField } from '@radix-ui/themes';
import { Button, Callout, FieldError } from '@/components/common';
import { WizardHeader, WizardScreen } from '@/components/layout';
import {
  getBirthdayAge,
  getBirthdayInputBounds,
  isBirthdayAgeValid,
  MAX_AGE,
  MIN_AGE,
} from '@/lib/birthday';
import { fieldLabelStyle } from './styles';

export const PENDING_BIRTHDAY_KEY = 'pulp.pendingBirthday';

type Translate = (key: string) => string;

const createBirthdaySchema = (t: Translate) =>
  z
    .object({
      birthday: z
        .string()
        .min(1, t('errors.birthdayRequired'))
        .check(z.iso.date(t('errors.birthdayInvalid'))),
    })
    .superRefine((data, ctx) => {
      if (isBirthdayAgeValid(data.birthday)) return;

      const age = getBirthdayAge(data.birthday);
      if (age === null) return;

      if (age < MIN_AGE) {
        ctx.addIssue({
          code: 'custom',
          message: t('signupBirthday.errors.under18'),
          path: ['birthday'],
        });
        return;
      }

      if (age > MAX_AGE) {
        ctx.addIssue({
          code: 'custom',
          message: t('errors.birthdayTooOld'),
          path: ['birthday'],
        });
      }
    });

type BirthdayFormValues = z.infer<ReturnType<typeof createBirthdaySchema>>;

/** v90 s-birthday-gate — DOB check before any sign-up method. */
export function BirthdayPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const birthdayBounds = useMemo(() => getBirthdayInputBounds(), []);
  const schema = useMemo(() => createBirthdaySchema(key => t(key)), [t]);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BirthdayFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { birthday: '' },
  });

  const openDatePicker = () => {
    dateInputRef.current?.showPicker?.();
  };

  const onValid = (data: BirthdayFormValues) => {
    sessionStorage.setItem(PENDING_BIRTHDAY_KEY, data.birthday);
    navigate('/sign-up');
  };

  return (
    <WizardScreen header={<WizardHeader onBack={() => navigate('/')} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Box>
          <Heading size="5" weight="light" mb="2">
            {t('signupBirthday.title')}
          </Heading>
          <Callout>{t('signupBirthday.callout')}</Callout>
        </Box>

        <form id="birthday-form" onSubmit={handleSubmit(onValid)} noValidate>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text size="1" color="gray" style={fieldLabelStyle}>
                {t('signupBirthday.dateLabel')}
              </Text>
              <Controller
                name="birthday"
                control={control}
                render={({ field }) => (
                  <TextField.Root
                    ref={element => {
                      field.ref(element);
                      (dateInputRef as React.MutableRefObject<HTMLInputElement | null>).current =
                        element;
                    }}
                    type="date"
                    className="hide-native-date-icon"
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                    min={birthdayBounds.min}
                    max={birthdayBounds.max}
                    aria-label={t('signupBirthday.dateLabel')}
                    data-testid="birthday-date"
                    aria-invalid={errors.birthday ? true : undefined}
                    style={{ colorScheme: 'dark' }}
                  >
                    <TextField.Slot
                      side="right"
                      pr="3"
                      style={{ cursor: 'pointer' }}
                      onClick={openDatePicker}
                    >
                      <Calendar size={16} aria-hidden />
                    </TextField.Slot>
                  </TextField.Root>
                )}
              />
              <FieldError message={errors.birthday?.message} />
            </Flex>
          </Flex>
        </form>

        <Flex direction="column" gap="2">
          <Button type="submit" form="birthday-form" size="lg" color="gray" highContrast fullWidth>
            {t('signupBirthday.submit')}
          </Button>
        </Flex>
      </Flex>
    </WizardScreen>
  );
}
